import { computed, onBeforeUnmount, ref, type Ref } from "vue";
import { DISMISS_TICK_MS } from "@/src/design_system/feedback/alert/constants/dismiss";

interface UseAutoDismissOptions {
  duration: Ref<number>;
  onTimeout: () => void;
  dismissEnabled: boolean;
}

export const useAutoDismiss = ({ duration, onTimeout, dismissEnabled }: UseAutoDismissOptions) => {
  const remainingMs = ref(duration.value);
  const isRunning = ref(false);
  const isPaused = ref(false);

  let timerId: ReturnType<typeof setInterval> | null = null;

  const clearTimer = () => {
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
    isRunning.value = false;
  };

  const resetRemaining = () => {
    remainingMs.value = Math.max(0, duration.value);
  };

  const handleTimeout = () => {
    clearTimer();
    onTimeout();
  };

  const tick = () => {
    if (isPaused.value) return;
    remainingMs.value = Math.max(0, remainingMs.value - DISMISS_TICK_MS);

    if (remainingMs.value === 0) {
      handleTimeout();
    }
  };

  const start = () => {
    if (!dismissEnabled) return;

    clearTimer();
    resetRemaining();

    if (remainingMs.value === 0) {
      handleTimeout();
      return;
    }

    isPaused.value = false;
    timerId = setInterval(tick, DISMISS_TICK_MS);
    isRunning.value = true;
  };

  const stop = () => {
    clearTimer();
    resetRemaining();
    isPaused.value = false;
  };

  const pause = () => {
    if (!isRunning.value || !dismissEnabled) return;
    isPaused.value = true;
  };

  const resume = () => {
    if (!isRunning.value || !dismissEnabled) return;
    isPaused.value = false;
  };

  const progress = computed(() => {
    if (duration.value === 0) return 0;
    const ratio = remainingMs.value / duration.value;
    return Math.max(0, Math.min(100, Math.round(ratio * 100)));
  });

  onBeforeUnmount(() => {
    clearTimer();
  });

  return {
    progress,
    start,
    stop,
    pause,
    resume,
  };
};
